import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { FaKey, FaSave } from 'react-icons/fa';
import { useTheme } from '../ThemeContext';

// Global Admin → Passkeys. Platform-level WebAuthn config for signing in without a
// password. Nothing here is a secret: WebAuthn has no shared secret, and the RP ID is
// inside every options payload a browser already receives.
//
// The RP ID is the part that deserves care. A credential is permanently bound to the
// domain it was created under, so editing this value does not migrate anything — it
// orphans every passkey already enrolled. The screen therefore shows how many exist and
// how many would be orphaned BEFORE the change, rather than leaving users to find out at
// their next sign-in.
function PasskeySettings() {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [cfg, setCfg] = useState({
    enabled: false, rp_id: '', origins: '', enrolled_count: 0, orphaned_count: 0
  });

  const adminToken = localStorage.getItem('Admintoken');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/s1/admin/passkey-settings', {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load passkey settings');
      if (data.settings) setCfg(data.settings);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch('/s1/admin/passkey-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ enabled: cfg.enabled, rpId: cfg.rp_id, origins: cfg.origins })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save passkey settings');
      if (data.settings) setCfg(data.settings);
      setMessage('Passkey settings saved. Changes take effect within a few seconds.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center p-5"><Spinner animation="border" /></div>;
  }

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));
  // Only meaningful once an RP ID is set: with none configured every passkey counts as
  // "other", which would read as alarming rather than informative.
  const orphaning = Boolean(cfg.rp_id) && cfg.orphaned_count > 0;

  return (
    <Card className={isDarkMode ? 'bg-dark text-light' : ''}>
      <Card.Header className="d-flex align-items-center gap-2">
        <FaKey />
        <strong>Passkeys</strong>
        <Badge bg={cfg.enabled ? 'success' : 'secondary'} className="ms-2">
          {cfg.enabled ? 'Enabled' : 'Disabled'}
        </Badge>
        {cfg.enrolled_count > 0 && (
          <span className="ms-auto small text-muted">
            {cfg.enrolled_count} passkey{cfg.enrolled_count === 1 ? '' : 's'} enrolled
          </span>
        )}
      </Card.Header>

      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {message && <Alert variant="success">{message}</Alert>}

        <p className="text-muted">
          Lets people sign in with a fingerprint, face or device PIN instead of a password.
          A passkey replaces the password <em>and</em> the two-factor code, because the
          device verifies the person before it will sign.
        </p>

        <Form.Check
          type="switch"
          id="passkeys-enabled"
          className="mb-3"
          label="Allow passkeys across the platform"
          checked={cfg.enabled}
          onChange={(e) => set({ enabled: e.target.checked })}
        />

        {/* Turning it off is not destructive, and saying so prevents the cautious
            administrator from deleting credentials "to be safe". */}
        {!cfg.enabled && cfg.enrolled_count > 0 && (
          <Alert variant="secondary">
            Existing passkeys are kept, not deleted. People can still see and remove them
            in their own settings — they just cannot be used to sign in while this is off.
          </Alert>
        )}

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Relying Party ID</Form.Label>
              <Form.Control
                type="text"
                placeholder="app.pipsqueaknetscan.com"
                value={cfg.rp_id}
                onChange={(e) => set({ rp_id: e.target.value })}
              />
              <Form.Text className={isDarkMode ? 'text-light-50' : 'text-muted'}>
                The bare domain people sign in on — no <code>https://</code>, no port, no
                path. Use the parent domain (<code>pipsqueaknetscan.com</code>) only if one
                passkey must work across several subdomains.
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Allowed origins <span className="text-muted">(optional)</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="https://app.pipsqueaknetscan.com"
                value={cfg.origins}
                onChange={(e) => set({ origins: e.target.value })}
              />
              <Form.Text className={isDarkMode ? 'text-light-50' : 'text-muted'}>
                Comma separated. Leave blank for <code>https://{cfg.rp_id || '<rp id>'}</code>.
                {/* The one combination that passes the browser's check and then fails the
                    server's, which is otherwise a very confusing half-hour. */}
                <strong> Required</strong> if the RP ID is a parent domain — list every
                subdomain, or sign-in will fail verification.
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        {orphaning && (
          <Alert variant="warning">
            <strong>{cfg.orphaned_count}</strong> of the {cfg.enrolled_count} enrolled
            passkey{cfg.enrolled_count === 1 ? '' : 's'} were created under a different
            Relying Party ID. A passkey is permanently bound to the domain it was made on
            and cannot be moved — those people will need to remove theirs and enrol again.
          </Alert>
        )}

        <Button variant="primary" onClick={save} disabled={saving}>
          {saving ? <Spinner as="span" size="sm" animation="border" className="me-2" /> : <FaSave className="me-2" />}
          Save passkey settings
        </Button>
      </Card.Body>
    </Card>
  );
}

export default PasskeySettings;
